import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  debounce, 
  throttle, 
  memoize, 
  MemoizeOptions, 
  rafThrottle, 
  measurePerformance
} from '../utils/performanceUtils';

/**
 * Hook to memoize a function
 * @param fn Function to memoize
 * @param options Memoization options
 * @param deps Dependencies array (similar to useCallback)
 * @returns Memoized function
 */
export function useMemoizedCallback<T extends (...args: any[]) => any>(
  fn: T,
  options: MemoizeOptions = {},
  deps: React.DependencyList = []
): T {
  // Create a stable reference to options
  const optionsRef = useRef(options);
  
  return useCallback(
    memoize(fn, optionsRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps
  );
}

/**
 * Hook for debounced value
 * @param value Value to debounce
 * @param delay Delay in milliseconds
 * @param options Additional options for the debounce
 * @returns Debounced value
 */
export function useDebouncedValue<T>(
  value: T, 
  delay: number,
  options: { leading?: boolean; maxWait?: number } = {}
): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const optionsRef = useRef(options);
  
  useEffect(() => {
    // Create the debounced setter function
    const debouncedSetValue = debounce(
      (newValue: T) => {
        setDebouncedValue(newValue);
      },
      delay,
      optionsRef.current
    );
    
    // Update the debounced value
    debouncedSetValue(value);
    
    // Cleanup
    return () => {
      debouncedSetValue.cancel();
    };
  }, [value, delay]);
  
  return debouncedValue;
}

/**
 * Hook for debounced function with typings for cancel and flush methods
 * @param fn Function to debounce
 * @param delay Delay in milliseconds
 * @param options Additional options for the debounce function
 * @param deps Dependencies array
 * @returns Debounced function with cancel and flush methods
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  fn: T,
  delay: number,
  options: { leading?: boolean; maxWait?: number } = {},
  deps: React.DependencyList = []
): {
  (...args: Parameters<T>): void;
  cancel: () => void;
  flush: (...args: Parameters<T>) => ReturnType<T> | undefined;
} {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callback = useCallback(fn, deps);
  
  // Create a stable reference to options
  const optionsRef = useRef(options);
  
  const debouncedCallback = useMemo(
    () => debounce(callback, delay, optionsRef.current),
    [callback, delay]
  );
  
  // Cleanup when component unmounts or dependencies change
  useEffect(() => {
    return () => {
      debouncedCallback.cancel();
    };
  }, [debouncedCallback]);
  
  return debouncedCallback;
}

/**
 * Hook for throttled function with proper cancellation
 * @param fn Function to throttle
 * @param limit Time limit in milliseconds
 * @param options Additional options for the throttle function 
 * @param deps Dependencies array
 * @returns Throttled function with cancel method
 */
export function useThrottledCallback<T extends (...args: any[]) => any>(
  fn: T,
  limit: number,
  options: { leading?: boolean; trailing?: boolean } = {},
  deps: React.DependencyList = []
): {
  (...args: Parameters<T>): ReturnType<T> | undefined;
  cancel: () => void;
} {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callback = useCallback(fn, deps);
  
  // Create a stable reference to options
  const optionsRef = useRef(options);
  
  const throttledCallback = useMemo(
    () => throttle(callback, limit, optionsRef.current),
    [callback, limit]
  );
  
  // Cleanup when component unmounts or dependencies change
  useEffect(() => {
    return () => {
      throttledCallback.cancel();
    };
  }, [throttledCallback]);
  
  return throttledCallback;
}

/**
 * Hook for animation-frame based throttling (useful for scroll events, etc.)
 * @param fn Function to throttle with requestAnimationFrame
 * @param deps Dependencies array
 * @returns RAF-throttled function
 */
export function useRafCallback<T extends (...args: any[]) => void>(
  fn: T,
  deps: React.DependencyList = []
): (...args: Parameters<T>) => void {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callback = useCallback(fn, deps);
  
  return useMemo(
    () => rafThrottle(callback),
    [callback]
  );
}

/**
 * Hook to measure component render time
 * @param componentName The name of the component
 * @param options Configuration options for performance measurement
 */
export function useRenderMeasure(
  componentName: string, 
  options: { 
    enabled?: boolean;
    warnThreshold?: number;
    logLevel?: 'debug' | 'log' | 'warn' | 'error';
  } = {}
): void {
  const renderCount = useRef(0);
  const { 
    enabled = true, 
    warnThreshold = 16,
    logLevel = 'log' 
  } = options;
  
  // Only run the effect if enabled
  useEffect(() => {
    if (!enabled) return;
    
    // Measure the render time
    const startTime = performance.now();
    
    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      renderCount.current += 1;
      
      if (duration > warnThreshold) {
        console[warnThreshold > 50 ? 'warn' : logLevel](
          `⚠️ [Render] ${componentName} render #${renderCount.current} took ${Math.round(duration)}ms (exceeds threshold ${warnThreshold}ms)`
        );
      } else {
        console[logLevel](
          `🔄 [Render] ${componentName} render #${renderCount.current} took ${Math.round(duration)}ms`
        );
      }
    };
  });
}

/**
 * Hook for detecting slow component renders
 * (Keeping for backward compatibility)
 * @param componentName The name of the component 
 * @param threshold Render time threshold in milliseconds
 */
export function useDetectSlowRender(componentName: string, threshold: number = 16): void {
  useRenderMeasure(componentName, { 
    warnThreshold: threshold, 
    logLevel: 'warn',
    enabled: true
  });
}

/**
 * Hook for measuring performance of an arbitrary operation
 * @param label Label for the measurement
 * @param options Measurement options
 * @returns A function that when called will record the measurement
 */
export function useMeasure(
  label: string,
  options: { 
    threshold?: number; 
    logLevel?: 'debug' | 'log' | 'warn' | 'error';
    silent?: boolean;
    includeCallCount?: boolean;
  } = {}
): <T extends (...args: any[]) => any>(fn: T) => T {
  const callCount = useRef(0);
  const { includeCallCount = true, ...measureOptions } = options;
  
  return useCallback(<T extends (...args: any[]) => any>(fn: T): T => {
    callCount.current++;
    const countLabel = includeCallCount ? ` (call #${callCount.current})` : '';
    return measurePerformance(fn, `${label}${countLabel}`, measureOptions);
  }, [label, includeCallCount, measureOptions]);
}

/**
 * Hook for skipping expensive effects on frequent re-renders
 * @param effect Effect callback
 * @param deps Dependencies array
 * @param skipCount Number of renders to skip
 */
export function useSkippableEffect(
  effect: React.EffectCallback,
  deps: React.DependencyList,
  skipCount: number = 1
): void {
  const renderCount = useRef(0);
  
  useEffect(() => {
    renderCount.current += 1;
    
    if (renderCount.current % skipCount !== 0) {
      // Skip this effect run
      return undefined;
    }
    
    return effect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Hook for safely accessing previous value
 * @param value Current value
 * @returns Previous value
 */
export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  
  useEffect(() => {
    ref.current = value;
  }, [value]);
  
  return ref.current;
}

/**
 * Hook to detect layout thrashing (repeated reads/writes that force layout recalculation)
 * @param componentName The name of the component 
 * @returns Object with report function to be called in render
 */
export function useLayoutThrashingDetection(componentName: string): {
  reportDOMReads: (readCount: number) => void;
  reportDOMWrites: (writeCount: number) => void;
} {
  const domOperations = useRef({
    reads: 0,
    writes: 0,
    renderCount: 0
  });
  
  // Reset between renders
  useEffect(() => {
    // Report previous render's stats if above threshold
    const { reads, writes, renderCount } = domOperations.current;
    if (reads > 5 && writes > 5) {
      console.warn(
        `🚨 [Layout Thrashing] ${componentName} render #${renderCount} had ${reads} DOM reads and ${writes} DOM writes. Consider batching DOM operations.`
      );
    }
    
    // Increment render count for next cycle
    domOperations.current.renderCount++;
    
    // Reset counters
    domOperations.current.reads = 0;
    domOperations.current.writes = 0;
  });
  
  return {
    reportDOMReads: (readCount: number) => {
      domOperations.current.reads += readCount;
    },
    reportDOMWrites: (writeCount: number) => {
      domOperations.current.writes += writeCount;
    }
  };
}
